/**
 * NewDashboard — /dashboards/new
 * Create a dashboard on the FormPage template. Demo pages: /form-layouts (sections, footer actions), /profile
 * (account form). Server route createDashboard: `CanManageDashboards`.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createDashboardMutation, listDashboardsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { zDashboardCreate } from '../../generated/api/zod.gen.js';
import {
  Form,
  FormActions,
  FormFullWidth,
  FormSection,
  RadioGroupFormField,
  TextAreaFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { FormPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { useToast } from '../../components/ui/Toast.js';
import { PAGE_HELP } from '../../help/index.js';

const OWNER_OPTIONS = [
  { value: 'user', label: 'Personal', description: 'Visible only to you.' },
  { value: 'team', label: 'Team', description: 'Shared with members of your team.' },
  { value: 'org', label: 'Organization', description: 'Visible to everyone with dashboard access.' },
];

const HELP = PAGE_HELP['dashboards'];

const dashboardSchema = zDashboardCreate.pick({ name: true, description: true, ownerType: true });

export const NewDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const create = useMutation(createDashboardMutation());
  const form = useZodForm(dashboardSchema, { defaultValues: { name: '', description: '', ownerType: 'user' } });

  const submit = async (values: { name: string; description?: string | null; ownerType?: 'user' | 'team' | 'org' }) => {
    const result = await create.mutateAsync({ body: values });
    await queryClient.invalidateQueries({ queryKey: listDashboardsQueryKey() });
    notify('Dashboard created.');
    navigate(`/dashboards/${result.dashboard.id}`, { replace: true });
  };

  return (
    <FormPage
      title="New dashboard"
      subtitle="Pick who can see it, then add widgets."
      helpContent={HELP}
      backTo="/dashboards"
      backLabel="Dashboards"
    >
      <Can policy="CanManageDashboards">
        <Form form={form} onSubmit={submit} guardUnsaved>
          <FormSection title="Dashboard details">
            <FormFullWidth>
              <TextFormField name="name" label="Name" placeholder="Strategy performance" required />
            </FormFullWidth>
            <FormFullWidth>
              <TextAreaFormField
                name="description"
                label="Description"
                placeholder="What is this dashboard for?"
                rows={3}
                emptyAs={null}
              />
            </FormFullWidth>
            <FormFullWidth>
              <RadioGroupFormField
                name="ownerType"
                label="Who can see it"
                options={OWNER_OPTIONS}
                description="System dashboards are managed by the platform and cannot be created here."
              />
            </FormFullWidth>
            <FormActions submitLabel="Create dashboard" cancelTo="/dashboards" />
          </FormSection>
        </Form>
      </Can>
    </FormPage>
  );
};
